import { Button, FormField } from '../ui';
import ApiProvidersPanel from './ApiProvidersPanel';
import { useUI } from '../../context/UIContext';
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useCapabilities } from '../../hooks/useCapabilities';

const TARGETS = [
  ['profile:planner', 'Планирование'],
  ['profile:drafter', 'Подготовка материалов'],
  ['profile:compactor', 'Сжатие контекста'],
  ['profile:moderation', 'Наблюдение за безопасностью'],
  ['profile:image_generation', 'Генерация иллюстраций'],
  ['profile:visual_review', 'Визуальная проверка'],
  ['embedding', 'Поиск по смыслу'],
  ['developer', 'Разработчик'],
];
const EMPTY = { name: '', kind: 'acp', provider: 'codex', base_url: '', model: '', runner: 'docker', enabled: true, secret: '' };

const errorText = (error) => error?.response?.data?.detail || error?.message || 'Операция не выполнена';

export default function ConnectionsPanel() {
  const { confirm } = useUI();
  const capabilities = useCapabilities();
  const [data, setData] = useState({ connections: [], assignments: {} });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [auth, setAuth] = useState(null);
  const [authInput, setAuthInput] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.listConnections()); }
    catch (e) { setError(errorText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!auth?.attemptId || auth.status !== 'waiting') return undefined;
    const timer = window.setInterval(async () => {
      try {
        const result = await api.getConnectionAuth(auth.connectionId, auth.attemptId);
        setAuth(current => current?.attemptId === auth.attemptId ? { ...current, ...result } : current);
        if (result.status === 'complete') {
          setNotice('Вход выполнен; учётные данные сохранены в зашифрованном виде.');
          load();
        }
      } catch (e) {
        setAuth(current => current?.attemptId === auth.attemptId ? { ...current, status: 'failed' } : current);
        setError(errorText(e));
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [auth?.attemptId, auth?.connectionId, auth?.status, load]);

  const choose = (connection) => {
    setSelected(connection?.id || null);
    setDraft(connection ? { ...connection, secret: '' } : EMPTY);
    setError('');
    setNotice('');
  };

  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    const payload = Object.fromEntries(['name', 'kind', 'provider', 'model', 'options', 'runner', 'enabled', 'secret'].map(key => [key, draft[key]]));
    delete payload.id; delete payload.has_secret; delete payload.revision; delete payload.updated_at;
    delete payload.created_at;
    if (!payload.secret) delete payload.secret;
    payload.options = draft.options || {};
    const rates = draft.pricing;
    payload.pricing = draft.kind === 'llm' && rates && Object.values(rates).some(v => v !== '')
      ? { input: rates.input, cached_input: rates.cached_input, output: rates.output } : null;
    try {
      const result = selected
        ? await api.updateConnection(selected, payload)
        : await api.createConnection(payload);
      setSelected(result.id); setDraft({ ...result, secret: '' });
      setNotice('Подключение сохранено.'); await load();
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const assign = async (target, connectionId) => {
    setError('');
    try { await api.assignConnection(target, connectionId); await load(); }
    catch (e) { setError(errorText(e)); }
  };

  const startAuth = async () => {
    setError('');
    try {
      const result = await api.startConnectionAuth(selected);
      setAuth({ connectionId: selected, attemptId: result.attempt_id, status: 'waiting', output: '' });
    } catch (e) { setError(errorText(e)); }
  };

  const cancelAuth = async () => {
    if (auth?.attemptId) {
      try { await api.cancelConnectionAuth(auth.connectionId, auth.attemptId); }
      catch (e) { setError(errorText(e)); }
    }
    setAuth(null);
  };

  const clearSecret = async () => {
    if (!await confirm('Удалить сохранённые учётные данные этого подключения?')) return;
    try { await api.clearConnectionSecret(selected); setNotice('Учётные данные удалены.'); await load(); }
    catch (e) { setError(errorText(e)); }
  };

  const check = async () => {
    setBusy(true); setError(''); setNotice('');
    try { await api.checkConnection(selected); setNotice('Подключение ответило успешно.'); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };

  const kindChanged = (kind) => setDraft(current => ({
    ...current, kind, options: {}, provider: kind === 'acp' ? 'codex' : 'openai',
  }));
  const available = (target) => data.connections.filter(connection => (connection.available ?? connection.enabled) && (
    target === 'profile:image_generation' ? connection.kind === 'image_generation'
      : target === 'profile:visual_review' ? connection.kind === 'llm' && connection.options?.image_input === true
        : target === 'profile:moderation' ? connection.kind === 'llm' : target === 'embedding' ? connection.kind === 'embedding'
      : target === 'developer' ? ['acp', 'legacy_cli'].includes(connection.kind)
        : ['llm', 'acp'].includes(connection.kind)
  ));
  const savedConnection = data.connections.find(connection => connection.id === selected);
  const hasUnsavedChanges = savedConnection && (
    ['name', 'kind', 'provider', 'base_url', 'model', 'runner', 'enabled'].some(key => draft[key] !== savedConnection[key])
    || JSON.stringify(draft.options || {}) !== JSON.stringify(savedConnection.options || {})
    || JSON.stringify(draft.pricing || null) !== JSON.stringify(savedConnection.pricing || null)
    || !!draft.secret
  );

  return <div>
    <p className="text-muted">Модели и инструменты выбираются для всей платформы. Ключи и данные входа хранятся зашифрованными; после сохранения их нельзя посмотреть.</p>
    {error && <div className="alert alert-error">{error}</div>}
    {notice && <div className="alert alert-info">{notice}</div>}
    <ApiProvidersPanel connections={data.connections} onChange={load} />
    {capabilities.acp && <div className="connections-grid" style={{ marginBottom: 20 }}>
      <div className="card">
        <div className="card-header"><h2 className="card-title">ACP и CLI</h2><Button className="btn btn-secondary btn-sm" onClick={() => choose(null)}>Новое</Button></div>
        {!data.connections.some(c => ['acp', 'legacy_cli'].includes(c.kind)) && <p className="text-muted">Пока нет подключений.</p>}
        {data.connections.filter(c => ['acp', 'legacy_cli'].includes(c.kind)).map(connection => <Button key={connection.id} type="button"
          className="btn btn-secondary" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 8 }}
          onClick={() => choose(connection)}>
          <strong>{connection.name}</strong><br />
          <span className="text-muted">{connection.provider} · {connection.model || 'модель по умолчанию'} · {connection.has_secret ? 'ключ сохранён' : 'нет ключа'}{!connection.enabled ? ' · отключено' : ''}</span>
        </Button>)}
      </div>
      <div className="card">
        <h2 className="card-title">{selected ? 'Настроить подключение' : 'Новое подключение'}</h2>
        <form onSubmit={save}>
          <FormField label={<>Название</>}>
          <input className="form-input" required value={draft.name} onChange={e => setDraft({
              ...draft,
              name: e.target.value
            })} />
          </FormField>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            <FormField label={<>Тип</>}>
            <select className="form-input" value={draft.kind} onChange={e => kindChanged(e.target.value)}>{capabilities.acp && <option value="acp">ACP-агент</option>}{draft.kind === 'legacy_cli' && <option value="legacy_cli">Старый CLI</option>}</select>
            </FormField>
            <FormField label={<>Провайдер</>}>
            <select className="form-input" value={draft.provider} onChange={e => setDraft({
                ...draft,
                provider: e.target.value
              })}>
                          {(draft.kind === 'acp' ? [['codex', 'Codex'], ['claude', 'Claude Code']] : draft.kind === 'legacy_cli' ? [['opencode', 'OpenCode'], ['dsh', 'DSH']] : [['openai', 'OpenAI-совместимый API']]).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                        </select>
            </FormField>
            {draft.kind === 'acp' && <FormField label={<>Исполнитель</>}>
                                     <select className="form-input" value={draft.runner} onChange={e => setDraft({
                                         ...draft,
                                         runner: e.target.value
                                       })}><option value="docker">Docker</option><option value="local">Локальный</option></select>
                                     </FormField>}
          </div>
          {draft.kind !== 'legacy_cli' && <FormField label={<>Модель{draft.kind === 'acp' ? ' (пусто — выбор провайдера)' : ''}</>}>
                                          <input className="form-input" value={draft.model} onChange={e => setDraft({
                                              ...draft,
                                              model: e.target.value
                                            })} />
                                          </FormField>}
          <FormField label={<>API-ключ{selected && draft.has_secret ? ' (оставьте пустым, чтобы сохранить текущий)' : ''}</>}>
          <input className="form-input" type="password" autoComplete="new-password" value={draft.secret} onChange={e => setDraft({
              ...draft,
              secret: e.target.value
            })} />
          </FormField>
          <label style={{ display: 'block', marginBottom: 12 }}><input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} /> Включено</label>
          <div className="flex gap-2"><Button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Сохранение…' : 'Сохранить'}</Button>
            {selected && draft.kind === 'acp' && <Button type="button" className="btn btn-secondary" onClick={startAuth}>Войти через браузер</Button>}
            {selected && draft.kind !== 'legacy_cli' && <Button type="button" className="btn btn-secondary" disabled={busy || hasUnsavedChanges} onClick={check}>Проверить</Button>}
            {selected && draft.has_secret && <Button type="button" className="btn btn-danger" onClick={clearSecret}>Удалить ключ / выйти</Button>}
          </div>
          {hasUnsavedChanges && <p className="form-hint">Сначала сохраните изменения, затем проверьте подключение.</p>}
        </form>
        {auth && <div className="card" style={{ marginTop: 16 }}>
          <strong>Вход: {auth.status === 'waiting' ? 'ожидание' : auth.status === 'complete' ? 'готово' : 'ошибка'}</strong>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{auth.output || 'Ожидаем ссылку и код от инструмента…'}</pre>
          {auth.status === 'waiting' && <div className="flex gap-2"><input className="form-input" value={authInput} onChange={e => setAuthInput(e.target.value)} placeholder="Код подтверждения, если запрошен" /><Button className="btn btn-secondary" onClick={async () => { try { await api.sendConnectionAuthInput(auth.connectionId, auth.attemptId, authInput); setAuthInput(''); } catch (e) { setError(errorText(e)); } }}>Отправить</Button></div>}
          <Button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={cancelAuth}>{auth.status === 'waiting' ? 'Отменить' : 'Закрыть'}</Button>
        </div>}
      </div>
    </div>}
    <div className="card"><h2 className="card-title">Назначения по умолчанию</h2>
      <div className="grid-3">{TARGETS.filter(([target]) => capabilities.code_execution || target !== 'developer').map(([target, label]) => <div className="form-group" key={target}><FormField label={<>{label}</>}>
                                                                                                                                                                                     <select className="form-input" value={data.assignments[target] || ''} onChange={e => assign(target, e.target.value)}><option value="">Не назначено</option>{data.assignments[target] && !available(target).some(c => c.id === data.assignments[target]) && <option value={data.assignments[target]} disabled>{data.connections.find(c => c.id === data.assignments[target])?.name || 'Модель'} · Недоступна</option>}{available(target).map(connection => <option value={connection.id} key={connection.id}>{connection.provider_name ? `${connection.provider_name} / ` : ''}{connection.name}</option>)}</select>
                                                                                                                                                                                     </FormField></div>)}</div>
      <p className="text-muted">Индивидуальную модель агента можно выбрать на странице «Агенты».</p>
    </div>
  </div>;
}
